import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import {
  Rocket, Settings2, Plus, Play, Pause, SkipForward, Check, Loader2,
  FileText, ClipboardCheck, ScanSearch, FormInput, Send, ShieldCheck,
  Sparkles, Link2, ChevronRight, CircleDashed, X, Building2, ArrowLeft,
} from 'lucide-react';
import {
  getAutoApplySettings, updateAutoApplySettings, getAutoApplyQueue,
  addAutoApplyJobs, runAutoApply, skipAutoApplyJob,
  ingestBoardJobs, aggregateJobs,
} from '../services/api';

/* ----------------------------------------------------------------------------
 * Candidate Auto-Apply — jobright.ai-style. Neutral slate + single signal-green
 * accent. Left: agent panel (job queue + per-job 5-step timeline). Right: the
 * in-app "Application Site" preview that autofills + the resume-generation state.
 * Agent Settings is a separate view (Supervised/Automated, weekly objective,
 * primary resume, two toggles).
 * -------------------------------------------------------------------------- */

const GREEN = '#2563EB';
const GREEN_SOFT = '#EFF4FE';
const GREEN_INK = '#1D4FD8';
const GREEN_LINE = '#BFDBFE';

// The 5 application steps (mirrors jobright), each with its driving status + icon.
const STEPS = [
  { key: 'tailoring', label: 'Generate Custom Resume', icon: FileText },
  { key: 'confirm', label: 'Confirm', icon: ClipboardCheck },
  { key: 'analyzing', label: 'Analyze Application Site', icon: ScanSearch },
  { key: 'filling', label: 'Fill Out Application Form', icon: FormInput },
  { key: 'submitted', label: 'Submit', icon: Send },
];

// Map a job row's status -> its step index (0..4) and whether it's terminal.
const STATUS_STEP = { queued: -1, tailoring: 0, confirm: 1, analyzing: 2, filling: 3, submitted: 4, skipped: -1, failed: -1 };
const TERMINAL = ['submitted', 'skipped', 'failed'];

const reveal = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
};

function StatusPill({ status }) {
  const map = {
    queued: { bg: '#F1F5F9', fg: '#475569', label: 'Queued' },
    submitted: { bg: GREEN_SOFT, fg: GREEN_INK, label: 'Submitted' },
    skipped: { bg: '#F1F5F9', fg: '#64748B', label: 'Skipped' },
    failed: { bg: '#FEF2F2', fg: '#B91C1C', label: 'Failed' },
  };
  const s = map[status] || { bg: GREEN_SOFT, fg: GREEN_INK, label: 'Executing' };
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold"
      style={{ background: s.bg, color: s.fg, border: `1px solid ${status === 'failed' ? '#FECACA' : status === 'submitted' ? GREEN_LINE : '#E2E8F0'}` }}>
      {!TERMINAL.includes(status) && status !== 'queued' && <Loader2 size={12} className="animate-spin" />}
      {s.label}
    </span>
  );
}

/** Per-job vertical timeline of the 5 steps with checkmarks / in-progress dots. */
function StepTimeline({ job }) {
  const activeIdx = STATUS_STEP[job.status] ?? -1;
  const isSubmitted = job.status === 'submitted';
  const failed = job.status === 'failed';

  return (
    <ol className="mt-4 space-y-0">
      {STEPS.map((step, i) => {
        const done = isSubmitted || i < activeIdx;
        const active = !isSubmitted && i === activeIdx;
        const Icon = step.icon;
        return (
          <li key={step.key} className="relative flex items-start gap-3 pb-4 last:pb-0">
            {/* connector line */}
            {i < STEPS.length - 1 && (
              <span className="absolute left-[15px] top-8 h-[calc(100%-1.5rem)] w-px"
                style={{ background: done ? GREEN_LINE : '#E2E8F0' }} />
            )}
            <span className="relative z-10 flex h-8 w-8 flex-none items-center justify-center rounded-full border"
              style={
                done
                  ? { background: GREEN, borderColor: GREEN, color: '#fff' }
                  : active
                    ? { background: '#fff', borderColor: GREEN, color: GREEN }
                    : { background: '#fff', borderColor: '#E2E8F0', color: '#94A3B8' }
              }>
              {done ? <Check size={15} strokeWidth={3} />
                : active ? <Loader2 size={14} className="animate-spin" />
                  : <Icon size={14} />}
            </span>
            <div className="pt-1">
              <div className={`text-sm font-medium ${done ? 'text-gray-900' : active ? 'text-gray-900' : 'text-gray-400'}`}>
                {step.label}
              </div>
              {active && (
                <div className="mt-0.5 text-xs" style={{ color: GREEN }}>
                  In progress…
                </div>
              )}
              {failed && i === 0 && (
                <div className="mt-0.5 text-xs text-red-500">{job.error || 'Something went wrong'}</div>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/** RIGHT panel — the in-app "Application Site" preview. */
function ApplicationSite({ job }) {
  if (!job) {
    return (
      <div className="flex h-full min-h-[420px] flex-col items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-white p-10 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl" style={{ background: GREEN_SOFT, color: GREEN }}>
          <ScanSearch size={22} />
        </div>
        <h3 className="mt-4 font-serif text-lg font-semibold text-gray-900">Application Site</h3>
        <p className="mt-1.5 max-w-xs text-sm text-gray-500">
          Select a job from the queue to preview its application form being analyzed and autofilled here.
        </p>
      </div>
    );
  }

  const stepIdx = STATUS_STEP[job.status] ?? -1;
  const generatingResume = job.status === 'tailoring';
  const analyzing = job.status === 'analyzing';
  const fields = job.autofill?.fields || [];
  const values = job.autofill?.values || {};

  // "Generating Custom Resume…" state
  if (generatingResume) {
    return (
      <div className="flex h-full min-h-[420px] flex-col rounded-2xl border border-gray-200 bg-white p-8">
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.06em]" style={{ color: GREEN }}>
          <Sparkles size={13} /> Generating Custom Resume…
        </div>
        <div className="mt-8 space-y-3">
          {[92, 78, 85, 64, 88, 70, 95, 60].map((w, i) => (
            <motion.div key={i} className="h-3 rounded-full bg-gray-100 overflow-hidden">
              <motion.div className="h-full rounded-full"
                style={{ background: i % 3 === 0 ? GREEN_SOFT : '#EEF2F6' }}
                initial={{ width: 0 }} animate={{ width: `${w}%` }}
                transition={{ duration: 0.6, delay: i * 0.12, repeat: Infinity, repeatType: 'reverse', repeatDelay: 0.8 }} />
            </motion.div>
          ))}
        </div>
        <div className="mt-auto flex items-center gap-2 pt-6 text-sm text-gray-500">
          <Loader2 size={15} className="animate-spin" style={{ color: GREEN }} />
          Tailoring your resume to {job.job_title || 'this role'}…
        </div>
      </div>
    );
  }

  // Application-form preview (analyze / fill / submitted)
  return (
    <div className="flex h-full min-h-[420px] flex-col rounded-2xl border border-gray-200 bg-white overflow-hidden">
      {/* faux browser chrome */}
      <div className="flex items-center gap-2 border-b border-gray-100 bg-gray-50/70 px-4 py-2.5">
        <span className="h-2.5 w-2.5 rounded-full bg-gray-300" />
        <span className="h-2.5 w-2.5 rounded-full bg-gray-300" />
        <span className="h-2.5 w-2.5 rounded-full bg-gray-300" />
        <div className="ml-2 flex flex-1 items-center gap-1.5 truncate rounded-md bg-white px-2.5 py-1 text-xs text-gray-400 border border-gray-200">
          <Link2 size={11} /> <span className="truncate">{job.job_url}</span>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-gray-400">
          <Building2 size={12} /> {job.company || 'Company'} · Application
        </div>
        <h3 className="mt-1.5 font-serif text-xl font-semibold text-gray-900">{job.job_title || 'Job Application'}</h3>

        {analyzing ? (
          <div className="mt-8 flex flex-col items-center justify-center py-10 text-center">
            <ScanSearch size={26} style={{ color: GREEN }} className="animate-pulse" />
            <p className="mt-3 text-sm font-medium text-gray-700">Analyzing application site…</p>
            <p className="mt-1 text-xs text-gray-500">Detecting the form fields this site requires.</p>
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            {fields.length === 0 && (
              <p className="text-sm text-gray-500">Form fields will appear here once the site is analyzed.</p>
            )}
            {fields.map((f, i) => {
              const v = values[f.name];
              const filled = v !== undefined && v !== null && v !== '';
              return (
                <motion.div key={f.name} {...reveal} transition={{ delay: i * 0.06 }}>
                  <label className="mb-1 block text-xs font-medium text-gray-500">
                    {f.label}{f.required && <span className="text-red-400"> *</span>}
                  </label>
                  {f.type === 'file' ? (
                    <div className="flex items-center gap-2 rounded-lg border px-3 py-2.5 text-sm"
                      style={filled ? { borderColor: GREEN_LINE, background: GREEN_SOFT } : { borderColor: '#E2E8F0' }}>
                      <FileText size={15} style={{ color: filled ? GREEN_INK : '#94A3B8' }} />
                      <span className={filled ? 'text-gray-800' : 'text-gray-400'}>{filled ? v : 'No file'}</span>
                      {filled && <Check size={14} className="ml-auto" style={{ color: GREEN }} />}
                    </div>
                  ) : f.type === 'boolean' ? (
                    <div className="flex items-center gap-2 rounded-lg border px-3 py-2.5 text-sm"
                      style={filled ? { borderColor: GREEN_LINE, background: GREEN_SOFT } : { borderColor: '#E2E8F0' }}>
                      <span className="text-gray-800">{filled ? (v ? 'Yes' : 'No') : '—'}</span>
                      {filled && <Check size={14} className="ml-auto" style={{ color: GREEN }} />}
                    </div>
                  ) : f.type === 'textarea' ? (
                    <div className="rounded-lg border px-3 py-2.5 text-sm min-h-[60px] whitespace-pre-wrap"
                      style={filled ? { borderColor: GREEN_LINE, background: GREEN_SOFT, color: '#1f2937' } : { borderColor: '#E2E8F0', color: '#9CA3AF' }}>
                      {filled ? (String(v).length > 220 ? String(v).slice(0, 220) + '…' : v) : 'Optional'}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 rounded-lg border px-3 py-2.5 text-sm"
                      style={filled ? { borderColor: GREEN_LINE, background: GREEN_SOFT } : { borderColor: '#E2E8F0' }}>
                      <span className={filled ? 'text-gray-800' : 'text-gray-400'}>{filled ? v : '—'}</span>
                      {filled && <Check size={14} className="ml-auto" style={{ color: GREEN }} />}
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* footer / submit state */}
      <div className="border-t border-gray-100 bg-gray-50/60 px-6 py-4">
        {job.autofill?.real && job.autofill?.applyUrl ? (
          <>
            <div className="mb-2 flex items-center gap-2 text-xs font-medium" style={{ color: GREEN_INK }}>
              <ShieldCheck size={14} style={{ color: GREEN }} /> Pre-filled from the live application form
            </div>
            <a
              href={job.autofill.applyUrl}
              target="_blank"
              rel="noreferrer"
              className="flex w-full items-center justify-center gap-1.5 rounded-lg py-2.5 text-sm font-semibold text-white"
              style={{ background: GREEN }}
            >
              Review &amp; submit on the application site <Link2 size={14} />
            </a>
            <p className="mt-2 text-[11px] text-gray-400">
              We pre-fill from your profile — you review and submit on the real site. We never submit without you.
            </p>
          </>
        ) : job.status === 'submitted' ? (
          <div className="flex items-center gap-2 text-sm font-medium" style={{ color: GREEN_INK }}>
            <ShieldCheck size={16} style={{ color: GREEN }} /> Application prepared (in-app)
          </div>
        ) : (
          <button disabled
            className="w-full rounded-lg py-2.5 text-sm font-semibold text-white opacity-60"
            style={{ background: GREEN }}>
            {stepIdx >= 3 ? 'Preparing…' : 'Prepare Application'}
          </button>
        )}
      </div>
    </div>
  );
}

/* ---------------------------- Agent Settings view --------------------------- */

function SelectCard({ active, onClick, title, desc, icon: Icon }) {
  return (
    <button onClick={onClick}
      className="flex flex-col items-start rounded-2xl border p-5 text-left transition-all"
      style={active
        ? { borderColor: GREEN, background: GREEN_SOFT, boxShadow: `0 0 0 1px ${GREEN}` }
        : { borderColor: '#E5E7EB', background: '#fff' }}>
      <div className="flex h-10 w-10 items-center justify-center rounded-xl"
        style={{ background: active ? '#fff' : GREEN_SOFT, color: GREEN }}>
        <Icon size={18} />
      </div>
      <div className="mt-3 flex items-center gap-1.5">
        <span className="font-semibold text-gray-900">{title}</span>
        {active && <Check size={15} style={{ color: GREEN }} />}
      </div>
      <p className="mt-1 text-sm text-gray-500">{desc}</p>
    </button>
  );
}

function Toggle({ checked, onChange, label, desc }) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-2xl border border-gray-200 bg-white p-5">
      <div>
        <div className="font-medium text-gray-900">{label}</div>
        <p className="mt-0.5 text-sm text-gray-500">{desc}</p>
      </div>
      <button onClick={() => onChange(!checked)} type="button"
        className="relative mt-1 inline-flex h-6 w-11 flex-none items-center rounded-full transition-colors"
        style={{ background: checked ? GREEN : '#CBD5E1' }}>
        <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-5' : 'translate-x-0.5'}`} />
      </button>
    </div>
  );
}

function AgentSettings({ settings, onSave, saving, onBack }) {
  const [form, setForm] = useState(settings);
  useEffect(() => setForm(settings), [settings]);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const OBJECTIVES = [
    { key: 'under_20', title: 'Under 20 / week', desc: 'A focused, selective search.' },
    { key: '20_to_50', title: '20–50 / week', desc: 'A steady, active search.' },
    { key: 'over_50', title: 'Over 50 / week', desc: 'A high-volume, aggressive search.' },
  ];

  return (
    <motion.div {...reveal} transition={{ duration: 0.3 }} className="mx-auto max-w-3xl">
      <button onClick={onBack} className="mb-5 inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-900">
        <ArrowLeft size={15} /> Back to agent
      </button>

      <h2 className="font-serif text-2xl font-semibold text-gray-900">Agent Settings</h2>
      <p className="mt-1 text-gray-500">Tune how the auto-apply agent works on your behalf.</p>

      {/* Mode */}
      <div className="mt-8">
        <div className="text-xs font-semibold uppercase tracking-[0.06em]" style={{ color: GREEN }}>Mode</div>
        <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <SelectCard active={form.mode === 'supervised'} onClick={() => set({ mode: 'supervised' })}
            title="Supervised" desc="You review and confirm each application before it's submitted." icon={ClipboardCheck} />
          <SelectCard active={form.mode === 'automated'} onClick={() => set({ mode: 'automated' })}
            title="Automated" desc="The agent runs the full queue end-to-end without stopping." icon={Rocket} />
        </div>
      </div>

      {/* Objective */}
      <div className="mt-8">
        <div className="text-xs font-semibold uppercase tracking-[0.06em]" style={{ color: GREEN }}>Weekly objective</div>
        <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {OBJECTIVES.map((o) => (
            <SelectCard key={o.key} active={form.weekly_objective === o.key}
              onClick={() => set({ weekly_objective: o.key })} title={o.title} desc={o.desc} icon={Sparkles} />
          ))}
        </div>
      </div>

      {/* Primary resume */}
      <div className="mt-8">
        <div className="text-xs font-semibold uppercase tracking-[0.06em]" style={{ color: GREEN }}>Primary resume</div>
        <p className="mt-1 text-sm text-gray-500">The resume the agent tailors from for every application.</p>
        <textarea
          value={form.primary_resume_text || ''}
          onChange={(e) => set({ primary_resume_text: e.target.value })}
          rows={6}
          placeholder="Paste your resume text here…"
          className="mt-3 w-full rounded-2xl border border-gray-200 bg-white p-4 text-sm text-gray-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>

      {/* Toggles */}
      <div className="mt-8 space-y-3">
        <Toggle checked={!!form.customize_resume} onChange={(v) => set({ customize_resume: v })}
          label="Customize resume for each application"
          desc="Re-tailor your resume to each job's description (uses Claude Haiku)." />
        <Toggle checked={!!form.generate_cover_letter} onChange={(v) => set({ generate_cover_letter: v })}
          label="Generate cover letter"
          desc="Draft a short, JD-specific cover letter for each application." />
      </div>

      <div className="mt-8 flex justify-end">
        <button onClick={() => onSave(form)} disabled={saving}
          className="inline-flex items-center gap-2 rounded-lg px-6 py-2.5 text-sm font-semibold text-white disabled:opacity-60"
          style={{ background: GREEN }}>
          {saving && <Loader2 size={15} className="animate-spin" />}
          Save settings
        </button>
      </div>
    </motion.div>
  );
}

/* ------------------------------- Add-jobs modal ----------------------------- */

function AddJobsModal({ open, onClose, onAdd, adding }) {
  const [text, setText] = useState('');
  const [company, setCompany] = useState('');
  const [ats, setAts] = useState('greenhouse');
  const [importing, setImporting] = useState(false);
  if (!open) return null;
  const urls = text.split('\n').map((s) => s.trim()).filter(Boolean);

  const doImport = async () => {
    if (!company.trim()) return;
    setImporting(true);
    try {
      const res = await ingestBoardJobs(ats, company.trim());
      toast.success(`Imported ${res.data.inserted} real jobs from ${company.trim()} (${res.data.skipped} already had).`);
      setCompany('');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Import failed — check the company board name.');
    } finally {
      setImporting(false);
    }
  };

  const doAggregate = async () => {
    setImporting(true);
    try {
      const res = await aggregateJobs();
      toast.success(`Imported ${res.data.inserted} jobs from the open feed.`);
    } catch (e) {
      toast.error('Could not import from the feed.');
    } finally {
      setImporting(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-lg rounded-2xl border border-gray-200 bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-lg font-semibold text-gray-900">Add jobs to the queue</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <p className="mt-1 text-sm text-gray-500">Paste one job link per line. <span className="font-medium text-gray-700">Greenhouse links autofill the real application form.</span></p>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={5}
          placeholder={'https://job-boards.greenhouse.io/airtable/jobs/8391589002\nhttps://jobs.lever.co/acme/role-id'}
          className="mt-4 w-full rounded-xl border border-gray-200 p-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
        <div className="mt-4 flex items-center justify-between">
          <span className="text-xs text-gray-400">{urls.length} link{urls.length === 1 ? '' : 's'}</span>
          <div className="flex gap-2">
            <button onClick={onClose} className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
            <button onClick={() => onAdd(urls)} disabled={!urls.length || adding}
              className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60" style={{ background: GREEN }}>
              {adding && <Loader2 size={14} className="animate-spin" />} Add {urls.length || ''}
            </button>
          </div>
        </div>

        {/* Import real jobs straight from an ATS (free, no key) */}
        <div className="mt-5 border-t border-gray-100 pt-4">
          <p className="text-xs font-semibold uppercase tracking-[0.06em] text-gray-400">Or import real jobs</p>
          <div className="mt-2 flex gap-2">
            <select value={ats} onChange={(e) => setAts(e.target.value)}
              className="rounded-lg border border-gray-200 px-2 py-2 text-sm focus:outline-none">
              <option value="greenhouse">Greenhouse</option>
              <option value="lever">Lever</option>
            </select>
            <input value={company} onChange={(e) => setCompany(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && doImport()}
              placeholder="company board name (e.g. airtable, stripe)"
              className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
            <button onClick={doImport} disabled={!company.trim() || importing}
              className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-white disabled:opacity-60" style={{ background: GREEN }}>
              {importing ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Import
            </button>
          </div>
          <button onClick={doAggregate} disabled={importing}
            className="mt-2 text-xs font-medium text-gray-500 underline hover:text-gray-700 disabled:opacity-60">
            or pull 50 from the open remote-jobs feed
          </button>
        </div>
      </motion.div>
    </div>
  );
}

/* --------------------------------- Page ------------------------------------ */

function AutoApply() {
  const [view, setView] = useState('agent'); // 'agent' | 'settings'
  const [settings, setSettings] = useState({
    mode: 'supervised', weekly_objective: 'under_20',
    primary_resume_text: '', customize_resume: true, generate_cover_letter: false,
  });
  const [jobs, setJobs] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const pollRef = useRef(null);

  const loadQueue = useCallback(async () => {
    try {
      const { data } = await getAutoApplyQueue();
      const list = data.jobs || [];
      setJobs(list);
      setSelectedId((prev) => prev || (list[0] && list[0].id) || null);
      return list;
    } catch (e) {
      return [];
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [s] = await Promise.all([getAutoApplySettings(), loadQueue()]);
        if (s?.data) setSettings((prev) => ({ ...prev, ...s.data }));
      } catch (e) {
        toast.error('Could not load auto-apply data');
      } finally {
        setLoading(false);
      }
    })();
  }, [loadQueue]);

  // Poll the queue while a run is in progress so step states update live.
  useEffect(() => {
    if (!running) {
      if (pollRef.current) clearInterval(pollRef.current);
      return undefined;
    }
    pollRef.current = setInterval(loadQueue, 1500);
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [running, loadQueue]);

  const remaining = jobs.filter((j) => !TERMINAL.includes(j.status)).length;
  const selected = jobs.find((j) => j.id === selectedId) || null;

  const handleSaveSettings = async (form) => {
    setSavingSettings(true);
    try {
      const { data } = await updateAutoApplySettings(form);
      setSettings((prev) => ({ ...prev, ...data }));
      toast.success('Settings saved');
      setView('agent');
    } catch (e) {
      toast.error('Could not save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleAdd = async (urls) => {
    setAdding(true);
    try {
      const { data } = await addAutoApplyJobs(urls);
      toast.success(`Added ${data.jobs?.length || 0} job(s)`);
      setAddOpen(false);
      const list = await loadQueue();
      if (!selectedId && list[0]) setSelectedId(list[0].id);
    } catch (e) {
      toast.error('Could not add jobs');
    } finally {
      setAdding(false);
    }
  };

  const handleRun = async (jobId) => {
    setRunning(true);
    try {
      await runAutoApply(jobId);
      await loadQueue();
      toast.success(jobId ? 'Application processed' : 'Queue processed');
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Run failed');
    } finally {
      setRunning(false);
      await loadQueue();
    }
  };

  const handleSkip = async (jobId) => {
    try {
      await skipAutoApplyJob(jobId);
      await loadQueue();
    } catch (e) {
      toast.error('Could not skip job');
    }
  };

  if (view === 'settings') {
    return <AgentSettings settings={settings} onSave={handleSaveSettings} saving={savingSettings} onBack={() => setView('agent')} />;
  }

  return (
    <div className="mx-auto max-w-7xl">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.06em]" style={{ color: GREEN }}>
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: GREEN }} /> Auto-Apply agent
          </div>
          <h1 className="mt-2 font-serif text-3xl font-semibold tracking-tight text-gray-900">Apply for you, on autopilot</h1>
          <p className="mt-1 text-gray-500">Add job links, tailor your resume to each, and let the agent fill out applications.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setAddOpen(true)}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">
            <Plus size={16} /> Add jobs
          </button>
          <button onClick={() => setView('settings')}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">
            <Settings2 size={16} /> Agent Settings
          </button>
        </div>
      </div>

      {/* Status bar */}
      <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-gray-200 bg-white px-5 py-3.5">
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-semibold"
            style={running ? { background: GREEN_SOFT, color: GREEN_INK, border: `1px solid ${GREEN_LINE}` } : { background: '#F1F5F9', color: '#475569', border: '1px solid #E2E8F0' }}>
            {running ? <Loader2 size={14} className="animate-spin" /> : <CircleDashed size={14} />}
            {running ? 'Executing' : 'Standby'}
          </span>
          <span className="text-sm text-gray-500">
            <span className="font-semibold text-gray-900">{remaining}</span> job{remaining === 1 ? '' : 's'} remaining
          </span>
          <span className="hidden text-xs text-gray-400 sm:inline">· {settings.mode === 'automated' ? 'Automated' : 'Supervised'} mode</span>
        </div>
        <div className="flex gap-2">
          {running ? (
            <button onClick={() => setRunning(false)}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">
              <Pause size={15} /> Pause
            </button>
          ) : (
            <button onClick={() => handleRun()} disabled={remaining === 0}
              className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              style={{ background: GREEN }}>
              <Play size={15} /> Run queue
            </button>
          )}
        </div>
      </div>

      {/* Two columns */}
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]">
        {/* LEFT: agent panel — job queue + per-job step timeline */}
        <div className="space-y-4">
          {loading ? (
            <div className="flex items-center gap-2 rounded-2xl border border-gray-200 bg-white p-8 text-sm text-gray-500">
              <Loader2 size={16} className="animate-spin" /> Loading queue…
            </div>
          ) : jobs.length === 0 ? (
            <div className="flex flex-col items-center rounded-2xl border border-dashed border-gray-200 bg-white p-10 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl" style={{ background: GREEN_SOFT, color: GREEN }}>
                <Rocket size={22} />
              </div>
              <h3 className="mt-4 font-serif text-lg font-semibold text-gray-900">Your queue is empty</h3>
              <p className="mt-1.5 max-w-xs text-sm text-gray-500">Add a few job links to get started — the agent will handle the rest.</p>
              <button onClick={() => setAddOpen(true)}
                className="mt-5 inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white" style={{ background: GREEN }}>
                <Plus size={16} /> Add jobs
              </button>
            </div>
          ) : (
            <AnimatePresence initial={false}>
              {jobs.map((job) => {
                const isSel = job.id === selectedId;
                return (
                  <motion.div key={job.id} {...reveal} layout
                    onClick={() => setSelectedId(job.id)}
                    className="cursor-pointer rounded-2xl border bg-white p-5 transition-all"
                    style={isSel ? { borderColor: GREEN, boxShadow: `0 0 0 1px ${GREEN}` } : { borderColor: '#E5E7EB' }}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate font-serif text-base font-semibold text-gray-900">
                          {job.job_title || 'Untitled role'}
                        </div>
                        <div className="mt-0.5 flex items-center gap-1.5 truncate text-sm text-gray-500">
                          <Building2 size={13} /> {job.company || new URL(job.job_url).hostname.replace('www.', '')}
                        </div>
                      </div>
                      <StatusPill status={job.status} />
                    </div>

                    <StepTimeline job={job} />

                    {/* per-job actions */}
                    <div className="mt-3 flex items-center gap-2 border-t border-gray-100 pt-3">
                      {!TERMINAL.includes(job.status) && (
                        <>
                          <button onClick={(e) => { e.stopPropagation(); handleRun(job.id); }} disabled={running}
                            className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50" style={{ background: GREEN }}>
                            <Play size={12} /> Apply now
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); handleSkip(job.id); }}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50">
                            <SkipForward size={12} /> Skip
                          </button>
                        </>
                      )}
                      <button onClick={(e) => { e.stopPropagation(); setSelectedId(job.id); }}
                        className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-gray-400 hover:text-gray-700">
                        Preview <ChevronRight size={13} />
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          )}
        </div>

        {/* RIGHT: Application Site preview */}
        <div className="lg:sticky lg:top-6 lg:self-start">
          <ApplicationSite job={selected} />
        </div>
      </div>

      <AddJobsModal open={addOpen} onClose={() => setAddOpen(false)} onAdd={handleAdd} adding={adding} />
    </div>
  );
}

export default AutoApply;
